import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link2 } from "lucide-react";
import { Button } from "@v2/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@v2/components/ui/select";
import { supabaseV2 } from "../../lib/supabase";
import { suggestEaName, type FreeEaName } from "../../lib/ea-suggest";
import type { Player } from "../../types";

// Giocatori della rosa senza nome EA: le statistiche del Mondo FC non li
// raggiungono. Per ognuno si sceglie tra i nomi EA del club ancora liberi,
// con quello piu' somigliante gia' proposto.
export function MissingEaPanel({
  teamId,
  players,
  freeNames,
  onOpenPlayer,
}: {
  teamId: string;
  players: Player[];
  freeNames: FreeEaName[];
  onOpenPlayer: (playerId: string) => void;
}) {
  const { t } = useTranslation("players");
  const queryClient = useQueryClient();
  // Scelte fatte a mano; per gli altri vale il suggerimento.
  const [picked, setPicked] = useState<Record<string, string>>({});

  const link = useMutation({
    mutationFn: async ({ playerId, eaName }: { playerId: string; eaName: string }) => {
      const { error } = await supabaseV2.from("players").update({ ea_account_handle: eaName }).eq("id", playerId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["players", teamId] });
      queryClient.invalidateQueries({ queryKey: ["free-ea-names", teamId] });
      queryClient.invalidateQueries({ queryKey: ["players-missing-ea-count"] });
    },
    onError: (e: any) => alert(t("detail.updateError", { message: e.message || String(e) })),
  });

  // Un nome gia' scelto per un giocatore non va riproposto agli altri.
  const choiceFor = (p: Player) => picked[p.id] ?? suggestEaName(freeNames, p.first_name, p.last_name)?.ea_name ?? "";

  return (
    <div className="rounded-[20px] bg-[hsl(var(--warn-soft))] px-5 py-4 space-y-3">
      <div>
        <p className="font-semibold">
          {t("missingEa.title", { count: players.length })}
        </p>
        <p className="text-[13px] text-muted-foreground mt-0.5">{t("missingEa.hint")}</p>
      </div>
      <div className="divide-y divide-border">
        {players.map((p) => {
          const value = choiceFor(p);
          const suggested = suggestEaName(freeNames, p.first_name, p.last_name)?.ea_name;
          return (
            <div key={p.id} className="py-2 flex flex-col sm:flex-row sm:items-center gap-2">
              <button
                type="button"
                onClick={() => onOpenPlayer(p.id)}
                className="min-w-0 flex-1 text-left text-sm font-medium truncate hover:underline"
              >
                {p.display_name}
                {p.role_code && <span className="ml-1.5 text-xs font-normal text-muted-foreground">{p.role_code}</span>}
              </button>
              {freeNames.length === 0 ? (
                <span className="text-xs text-muted-foreground">{t("missingEa.noneFree")}</span>
              ) : (
                <div className="flex items-center gap-2">
                  <Select value={value || undefined} onValueChange={(v) => setPicked((s) => ({ ...s, [p.id]: v }))}>
                    <SelectTrigger className="h-9 w-full sm:w-64 text-sm">
                      <SelectValue placeholder={t("missingEa.selectPh")} />
                    </SelectTrigger>
                    <SelectContent>
                      {freeNames.map((n) => (
                        <SelectItem key={n.ea_name} value={n.ea_name}>
                          {n.ea_name}
                          <span className="ml-2 text-xs text-muted-foreground">
                            {t("missingEa.matches", { count: n.matches })}
                            {n.ea_name === suggested ? ` · ${t("missingEa.suggested")}` : ""}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    className="h-9 shrink-0"
                    disabled={!value || link.isPending}
                    onClick={() => link.mutate({ playerId: p.id, eaName: value })}
                  >
                    <Link2 className="h-4 w-4" />
                    {t("missingEa.link")}
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-[13px] text-muted-foreground">{t("missingEa.manualNote")}</p>
    </div>
  );
}
